/**
 * MMX Food Gallery — the dish strip.
 *
 * The track holds one copy of the photos and duplicates itself until it can cover the viewport
 * plus a full loop, so the marquee keyframe always lands on an identical frame and the seam
 * never shows.
 */

const DEFAULT_SPEED = 40;

class MmxFoodGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mmx-food-track]');
    if (!this.track) return;

    this.items = Array.from(this.track.children);
    if (this.items.length === 0) return;
    if (this.dataset.marquee !== 'true') return;

    this.#build();

    // Card widths are container query units, so the loop distance changes with the viewport.
    this.lastWidth = this.offsetWidth;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.offsetWidth === this.lastWidth) return;
      this.lastWidth = this.offsetWidth;
      this.#build();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  #build() {
    const track = this.track;

    // Measure a single set: the clones from the previous pass would otherwise be counted in.
    track.classList.remove('is-animated');
    track.querySelectorAll('[data-mmx-food-clone]').forEach((clone) => clone.remove());

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const setWidth = track.scrollWidth;
    if (setWidth <= 0) return;

    const copies = Math.max(2, Math.ceil((window.innerWidth + setWidth) / setWidth) + 1);

    for (let copy = 1; copy < copies; copy += 1) {
      this.items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-mmx-food-clone', '');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        if (clone.tagName === 'A') clone.setAttribute('tabindex', '-1');
        track.appendChild(clone);
      });
    }

    // One loop travels exactly one copy of the set, so speed stays constant whatever the count.
    const speed = Math.max(Number(this.dataset.speed) || DEFAULT_SPEED, 1);
    track.style.setProperty('--mmx-food-shift', `${-(setWidth + gap)}px`);
    track.style.setProperty('--mmx-food-duration', `${(setWidth + gap) / speed}s`);
    track.classList.add('is-animated');
  }
}

if (!customElements.get('mmx-food-gallery')) {
  customElements.define('mmx-food-gallery', MmxFoodGallery);
}
